import { useContext, useEffect, useState, useCallback } from 'react';
import {
	getProductBySku,
	updateBasket,
	getProductPrice,
} from 'application/repositories/ecommerceRepository';
import { Container, ProductDetails } from 'ui/components';
import { LayoutFeature } from 'features/Layouts/Layout/LayoutFeature';
import { getPageContent } from 'application/repositories/pageContentRepository';
import { Heading } from 'ui/components';
import { ProductDescription } from 'ui/components/3-organisms/ProductDescription';
import { getNavigationData } from 'application/repositories/navigationRepository';
import logger from 'helpers/logger';
import { useDispatch, useSelector } from 'react-redux';
import {
	currency,
	currentCartId,
	setCart,
} from 'application/adapters/store/slices/cartSlice';
import { signIn } from 'next-auth/react';

import {
	useAuthenticationContext,
	getAccessToken,
} from 'application/contexts/AuthenticationContext';

import { ModalSignupFormFeature } from 'features/Modules';
import { GetServerSideProps } from 'next';
import { getSignupFormData } from 'application/repositories/formRepository';
import { getLanguages } from 'application/repositories/languagesRepository';
import { getLocations } from 'application/repositories/locationsRepository';
import { DictionaryContext } from 'application/adapters/context/Dictionary';
import { currentCustomer } from 'application/adapters/store/slices/customerSlice';
import { getCookie } from 'cookies-next';
import { LocaleContext } from 'application/adapters/context/LocaleContext';
import formatCartPrices from 'helpers/formatCartPrices';
import { useRouter } from 'next/router';
import { FeaturedProductsFeature } from 'features/Modules/FeaturedProducts';
import { getRelatedProductBySku } from 'application/repositories/relatedProductsRepository';

const Product: unknown = (props) => {
	const router = useRouter();
	const { product, accessToken, signupFormData, culture, mappedProducts } = props;
	const { productDescriptionItems } = product || {};
	const [relatedProductsId, setRelatedProductsId] = useState(null);
	const dispatch = useDispatch();
	const basketId = useSelector(currentCartId);
	const authentication = useAuthenticationContext();
	const dictionary = useContext(DictionaryContext);
	const businessPartner = useSelector(currentCustomer);
	const currencyCode = useSelector(currency);
	const [currentProduct, setCurrentProduct] =
		useState<Models.CategoryProduct>(null);

	const localeContext = useContext(LocaleContext);
	const countryChosen = localeContext.getLocaleCountry() !== '';

	const metaData = {
		title:
			product?.name &&
			`${product.name} | ${dictionary.getValue(
				'SiteMeta.TitleSuffix',
				null,
				'Struers',
			)}`,
		description: product?.shortDescription,
		customPageUrls: true,
	};

	useEffect(() => {
		setCurrentProduct({
			...product,
			price: {
				...product?.price,
				formattedPrice: localeContext.formatPrice(
					product.price?.price,
					product.price?.currencyCode,
				),
			},
		});
	
	}, [currentProduct?.sku, router.asPath, product, product?.price]);

	useEffect(() => {
		if (currentProduct?.sku) {
			fetchRelatedProducts();
		}
	}, [currentProduct?.sku]);

	const mappedProductDescriptionItems = productDescriptionItems
		?.filter((item) => !!item.text && !!item.safetyDatasheets)
		?.map((item) => ({
			...item,

			title: dictionary.getValue(
				`ProductDetails.${item.title?.replace(' ', '')}`,
				null,
				item.title,
			),
		}));
	

	function handleUpdateBasket(cartProduct) {
		updateBasket(accessToken, cartProduct, false, true).then((res) => {
			dispatch(
				setCart(formatCartPrices(res, localeContext, currencyCode)),
			);
		});
	}

	const handleGetProductPrice = async (data: Models.GetPriceRequest) => {
		const res = await getProductPrice(accessToken, data);
		product.price = res;
		setCurrentProduct({
			...product,
			price: {
				...product.price,
				formattedPrice: localeContext.formatPrice(
					res?.price,
					res?.currencyCode,
				),
			},
		});
	};

	const productDetailsDictionary = {
		productNumber: dictionary.getValue(
			'ProductDetails.ProductNumber',
			null,
			'Product no:',
		),
		transportHazard: dictionary.getValue(
			'ShoppingCart.TransportHazard',
			null,
			'Transport hazardous',
		),
		transportHazardInfo: dictionary.getValue(
			'ShoppingCart.TransportHazardInfo',
			null,
			'Transport hazardous info',
		),
		price: dictionary.getValue('Price', null, 'Price:'),
		signInToViewPrice: dictionary.getValue(
			'ProductDetails.SignInToViewPrice',
			null,
			'Sign in to view the price',
		),
		safetyDataSheet: dictionary.getValue(
			'ProductDetails.SafetyDataSheet',
			null,
			'Safety Data sheet',
		),
		downloadSafetyDataSheet: dictionary.getValue(
			'ProductDetails.DownloadSafetyDataSheet',
			null,
			'Download Safety Data sheet:',
		),
		addToCart: dictionary.getValue(
			'ShoppingCart.AddToCart',
			null,
			'Add to cart',
		),
		confirmAddedToCart: dictionary.getValue(
			'ShoppingCart.ConfirmAddedToCart',
			null,
			'Item added to cart',
		),
		chooseBusinessPartner: dictionary.getValue(
			'ShoppingCart.ChooseBusinessPartner',
			null,
			'You must first choose a business partner',
		),
		signInButton: dictionary.getValue(
			'Product.SignInButton',
			null,
			'Sign in or create account to buy',
		),
		fetchProductPrice: dictionary.getValue(
			'Product.FetchProductPrice',
			null,
			'Fetch my price',
		),
		waitText: dictionary.getValue(
			'Product.FetchProductPriceWaitText',
			null,
			'Hold on while we fetch your price',
		),
		quantityLabel: dictionary.getValue(
			'Product.QuantityLabel',
			null,
			'Qty.',
		),
		chooseCountryToDownload: dictionary.getValue(
			'ProductDetails.ChooseCountryToDownload',
			null,
			'Please choose a country to download the sheet.',
		),
	};
	const fetchRelatedProducts = async () => {
		await getRelatedProductBySku(currentProduct?.sku, accessToken).then(
			(res) => {
				const returnValue = {
					properties: {
						productsPicker: res?.join(','),
						title: dictionary.getValue(
							'Product.RelatedProducts',
							null,
							'Related products',
						)
					}
				}
				if (res) setRelatedProductsId(returnValue);
			},
		);
	};

	if (!product)
		return (
			<LayoutFeature metaData={metaData} {...props}>
				<Container width="Standard">
					<Heading
						headingLevel="h2"
						className="u-top-margin--xxxl u-bottom-margin--xxxl"
						textCenter
					>
						{dictionary.getValue(
							'Product.ProductNotFound',
							null,
							'The requested product could not be found.',
						)}
					</Heading>
				</Container>
			</LayoutFeature>
		);
	return (
		<LayoutFeature metaData={metaData} {...props}>
			<Container width="Standard">
				<ProductDetails
					dictionary={productDetailsDictionary}
					product={currentProduct}
					updateBasketCallback={handleUpdateBasket}
					getProductPriceCallback={handleGetProductPrice}
					basketId={basketId}
					profile={authentication?.user}
					onSignIn={() => {
						signIn('authServer');
					}}
					bpId={businessPartner?.id}
				/>
				{/* ContactForm / HelpForm to be enabled in a future iteration */}
				{/* <ModalHelpFormFeature
					{...contactFormData}
				></ModalHelpFormFeature> */}
				<ModalSignupFormFeature
					accessToken={accessToken}
					{...signupFormData}
				/>
				<ProductDescription
					productDescriptionItems={mappedProductDescriptionItems}
					dictionary={productDetailsDictionary}
					productName={product.name}
					countryChosen={countryChosen}
				/>
				{relatedProductsId && <FeaturedProductsFeature accessToken={accessToken} culture={culture} content={relatedProductsId} />}

			</Container>
		</LayoutFeature>
	);
};

export default Product;

export const getServerSideProps: GetServerSideProps<any> = async ({
	req,
	params,
	preview,
	previewData: untypedPreviewData,
	locale,
}) => {
	try {
		const bpId = getCookie('bpId', { req })?.toString();
		const { host } = params;
		const url = params.page ? (params.page as string[]).join('/') : '';
		const previewData = untypedPreviewData as Models.PreviewData;
		const content = await getPageContent({
			url,
			host,
			preview,
			previewData,
			locale,
		});
		const culture = locale === 'default' ? 'en' : locale;
		const nextLocale = req.cookies.NEXT_LOCALE;
		// nextLocale is a format of 'da-DK'

		let [, countryCode] = nextLocale?.split('-') ?? ['', ''];

		if (!countryCode) {
			countryCode = '';
		}
		const accessTokenPromise = getAccessToken(req);
		const accessToken = (await accessTokenPromise) ?? null;
		const navigationData = await getNavigationData({
			host: host,
			accessToken,
			locale: culture,
		});

		const productId = params.slug[0];
		const product = await getProductBySku(
			productId,
			accessToken,
			culture,
			bpId,
			countryCode,
		);
		// ContactForm / HelpForm to be enabled in a future iteration
		// const contactFormData = await getContactFormData({
		// 	host,
		// 	locale: culture,
		// });

		const signupFormData = await getSignupFormData({
			host,
			accessToken,
			locale: culture,
		});

		const languageSelector = await getLanguages({
			host,
			locale: culture,
		});

		const locations = await getLocations({ host: host, locale: culture });
		return {
			props: {
				content,
				host,
				product,
				navigationData,
				accessToken,
				// contactFormData, // ContactForm/HelpForm to be enabled in a future iteration
				signupFormData,
				languageSelector,
				locations,
				culture,
			},
		};
	} catch (err) {
		if (err?.statusCode === 301 || err?.statusCode === 302) {
			return {
				redirect: {
					statusCode: err.statusCode,
					destination: err.url,
				},
			};
		}

		if (err?.statusCode === 404 || err?.statusCode === 500) {
			return {
				notFound: true,
			};
		}

		logger.debug(err);

		throw new Error(err);
	}
};
